/**
 * Shared phone-number parsing helpers used by every invite input path in the
 * root app (QuickInviteDialog, MagicLinkInvitePanel, MeetingCalendarPanel).
 *
 * The mobile package keeps its own copy so the package boundary stays intact,
 * but the logic must be kept in sync manually.
 */
/**
 * Clipboard text can contain Unicode bidi formatting around or inside a phone.
 * Remove only ALM, LRM/RLM, embeddings/overrides/PDF, and isolates/PDI — not
 * arbitrary invisible characters or invalid phone input.
 */
function stripDirectionFormatting(text) {
    return text.replace(/[\u061C\u200E\u200F\u202A-\u202E\u2066-\u2069]/g, "");
}
/** Best-effort E.164 normalization for invite phone inputs.
 * German national numbers use Germany as this panel's documented default:
 * `0171...` becomes `+49171...`. Existing `+` and `00` international forms
 * retain their country code. */
export function normalizePhone(raw) {
    if (typeof raw !== "string")
        return undefined;
    let value = stripDirectionFormatting(raw).trim().replace(/[\s\-()/.]/g, "");
    if (value.startsWith("00"))
        value = `+${value.slice(2)}`;
    else if (/^0[1-9]\d{4,14}$/.test(value))
        value = `+49${value.slice(1)}`;
    return /^\+\d{6,15}$/.test(value) ? value : undefined;
}
/**
 * Collapse separators inside phone-like sequences so numbers typed with
 * spaces, dashes, or other common separators are recognized as one canonical
 * E.164 phone token instead of being split into bogus "email" tokens.
 *
 * Handles both `+`-prefixed and `00`-prefixed international numbers:
 *   "+49 176 72651388"  → "+4917672651388"
 *   "0036 30 123-4567"  → "+36301234567"
 *
 * Boundary-anchored (start-of-entry or whitespace before, whitespace or end
 * after) so plus-addressed emails like "a+123-456@example.com" stay intact.
 */
export function collapsePhoneRuns(text) {
    // Pass 1 – collapse `+`-prefixed sequences (e.g. "+49 176 123-456").
    let result = stripDirectionFormatting(text).replace(/(^|\s)(\+\d[\d\s\-()/.]*\d)(?=\s|$)/g, (m, pre, run) => {
        const compact = run.replace(/[\s\-()/.]/g, "");
        return /^\+\d{6,15}$/.test(compact) ? pre + compact : m;
    });
    // Pass 2 – collapse `00`-prefixed sequences (e.g. "0036 30 123-4567") and
    // canonicalize the `00` to `+` so the rest of the pipeline only ever sees
    // E.164 format.
    result = result.replace(/(^|\s)(00\d[\d\s\-()/.]*\d)(?=\s|$)/g, (m, pre, run) => {
        const compact = run.replace(/[\s\-()/.]/g, "");
        const e164 = "+" + compact.slice(2);
        if (!/^\+\d{6,15}$/.test(e164))
            return m;
        return pre + e164;
    });
    // Pass 3 – German national format. This deliberately excludes `00`, which
    // was already handled above and must retain its explicit country code.
    result = result.replace(/(^|\s)(0[1-9]\d[\d\s\-()/.]*\d)(?=\s|$)/g, (m, pre, run) => {
        const e164 = normalizePhone(run);
        return e164 ? pre + e164 : m;
    });
    return result;
}
/**
 * Parse an invite textarea value into structured entries.
 *
 * Supported formats (one per line; lines may also be comma/semicolon-separated):
 *   alice@example.com
 *   Thomas <thomas@example.com>
 *   Thomas <thomas@example.com> +36301234567
 *   +36301234567
 *   Béla Kovács +36 30 123-4567
 *   0036 30 123-4567
 *   Thomas <thomas@example.com> 0036 30 123-4567
 *
 * Also supports plus-addressed emails ("a+tag@example.com") without treating
 * the `+tag` portion as a phone number (the boundary anchoring in
 * collapsePhoneRuns keeps them intact).
 *
 * Duplicate phone-only entries (same canonical number) are collapsed to one.
 */
export function parseEntries(text) {
    const out = [];
    const seenPhones = new Set();
    for (const raw of text.split(/[\n,;]+/)) {
        let part = collapsePhoneRuns(raw.trim());
        if (!part)
            continue;
        // Optional trailing phone (E.164) → the invite also goes out by SMS:
        // "Name <email> +3630…" or "email +3630…".
        const phoneMatch = part.match(/(?:^|\s)(\+\d{6,15})\s*$/);
        const phone = phoneMatch ? phoneMatch[1] : undefined;
        if (phoneMatch)
            part = part.slice(0, (phoneMatch.index ?? 0)).trim();
        const named = part.match(/^(.*?)<\s*([^<>\s]+)\s*>$/);
        if (named) {
            out.push({ email: named[2].trim(), name: named[1].trim() || undefined, phone });
        }
        else if (!part || (!part.includes("@") && phone)) {
            // Phone-only: no email address (empty remainder or no "@" character).
            // Collapse duplicates by phone number.
            if (phone && seenPhones.has(phone))
                continue;
            if (phone)
                seenPhones.add(phone);
            out.push({ name: part || undefined, phone });
        }
        else {
            // Plain entries may still be several space-separated addresses.
            for (const e of part.split(/\s+/))
                if (e)
                    out.push({ email: e });
            if (phone && out.length > 0)
                out[out.length - 1].phone = phone;
        }
    }
    return out;
}
